import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface VerificationStorage {
  [phoneNumber: string]: {
    code: string;
    expiresAt: number;
  };
}

interface CallableResult {
  success: boolean;
  requestId?: string;
  requestIds?: string[];
  message?: string;
  result?: any;
  results?: any[];
}

class SmsService {
  private static instance: SmsService;

  private constructor() {
    //console.log('SmsService 초기화 완료 (Firebase Functions 연결)');
  }

  public static getInstance(): SmsService {
    if (!SmsService.instance) {
      SmsService.instance = new SmsService();
    }
    return SmsService.instance;
  }

  // 인증번호 발송 (Firebase Functions Callable 사용)
  public async sendVerificationCode(phoneNumber: string): Promise<{ requestId: string }> {
    //console.log(`Firebase Functions Callable로 인증번호 발송 시도: ${phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}`);

    try {
      const sendVerificationCodeCallable = httpsCallable(functions, 'sendVerificationCodeCallable');
      
      const result = await sendVerificationCodeCallable({
        phoneNumber: phoneNumber
      });

      const data = result.data as CallableResult;
      //console.log('Firebase Functions 결과:', data);

      if (data.success) {
        //console.log(`인증번호 발송 성공: ${phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}`);
        
        return { 
          requestId: data.requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
      } else {
        throw new Error(`인증번호 발송 실패: ${data.message}`);
      }
    } catch (error: any) {
      //console.error('인증번호 발송 오류:', error);
      
      // Firebase Functions 오류 처리
      if (error.code) {
        switch (error.code) {
          case 'functions/unauthenticated':
            throw new Error('로그인이 필요합니다.');
          case 'functions/invalid-argument':
            throw new Error('전화번호가 올바르지 않습니다.');
          case 'functions/internal':
            throw new Error('서버 오류가 발생했습니다.');
          default:
            throw new Error(`인증번호 발송에 실패했습니다: ${error.message}`);
        }
      }
      
      throw new Error(`인증번호 발송에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  }

  // 회원가입 시 사용할 인증번호 발송 (인증 불필요)
  public async sendVerificationCodeForSignup(phoneNumber: string): Promise<{ requestId: string }> {
    //console.log(`회원가입용 인증번호 발송 시도: ${phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}`);

    try {
      const response = await fetch('https://asia-northeast3-bpnpmanage.cloudfunctions.net/sendVerificationCodeForSignup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber
        })
      });

      const data = await response.json();
      //console.log('회원가입용 인증번호 발송 결과:', data);

      if (data.success) {
        //console.log(`회원가입용 인증번호 발송 성공: ${phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}`);
        
        return { 
          requestId: data.requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
      } else {
        throw new Error(`인증번호 발송 실패: ${data.message}`);
      }
    } catch (error: any) {
      //console.error('회원가입용 인증번호 발송 오류:', error);
      
      if (error.message.includes('fetch')) {
        throw new Error('네트워크 오류가 발생했습니다.');
      }
      
      throw new Error(`인증번호 발송에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  }

  // 인증번호 검증 (Firebase Functions Callable 사용)
  public async verifyCode(phoneNumber: string, code: string): Promise<boolean> {
    //console.log(`Firebase Functions Callable로 인증번호 검증 시도: ${phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}, 입력코드: ${code}`);
    
    try {
      const verifyCodeCallable = httpsCallable(functions, 'verifyCodeCallable');
      
      const result = await verifyCodeCallable({
        phoneNumber: phoneNumber,
        code: code
      });

      const data = result.data as CallableResult;
      //console.log('인증번호 검증 결과:', data);

      if (data.success) {
        //console.log('인증번호 검증 성공');
        return true;
      } else {
        //console.log('인증번호 검증 실패:', data.message);
        return false;
      }
    } catch (error: any) {
      console.error('인증번호 검증 오류:', error);
      
      // Firebase Functions 오류 처리
      if (error.code) {
        switch (error.code) {
          case 'functions/unauthenticated':
            throw new Error('로그인이 필요합니다.');
          case 'functions/invalid-argument':
            throw new Error('전화번호와 인증번호를 입력해주세요.');
          default:
            console.error(`Functions 오류: ${error.message}`);
            return false;
        }
      }
      
      return false;
    }
  }

  // 회원가입 시 사용할 인증번호 검증 (인증 불필요)
  public async verifyCodeForSignup(phoneNumber: string, code: string): Promise<boolean> {
    //console.log(`회원가입용 인증번호 검증 시도: ${phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}, 입력코드: ${code}`);
    
    try {
      const response = await fetch('https://asia-northeast3-bpnpmanage.cloudfunctions.net/verifyCodeForSignup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          code: code
        })
      });

      const data = await response.json();
      //console.log('회원가입용 인증번호 검증 결과:', data);

      if (data.success) {
        //console.log('회원가입용 인증번호 검증 성공');
        return true;
      } else {
        //console.log('회원가입용 인증번호 검증 실패:', data.message);
        return false;
      }
    } catch (error: any) {
      console.error('회원가입용 인증번호 검증 오류:', error);
      
      if (error.message.includes('fetch')) {
        throw new Error('네트워크 오류가 발생했습니다.');
      }
      
      return false;
    }
  }

  // 일반 SMS 발송 (Firebase Functions Callable 사용)
  public async sendSMS(
    phoneNumber: string,
    message: string,
    options?: { subject?: string; subjects?: string[] }
  ): Promise<{ requestId: string; requestIds?: string[] }> {
    //console.log(`Firebase Functions Callable로 SMS 발송 시도: ${phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}`);

    try {
      const sendSMSCallable = httpsCallable(functions, 'sendSMSCallable');
      
      const result = await sendSMSCallable({
        phone: phoneNumber,
        content: message,
        ...(options?.subject ? { subject: options.subject } : {}),
        ...(options?.subjects?.length ? { subjects: options.subjects } : {})
      });

      const data = result.data as CallableResult;
      console.log('SMS 발송 결과:', data);

      if (data.success) {
        const requestIds =
          data.requestIds ??
          data.results?.map(r => r?.requestId).filter(Boolean) ??
          undefined;

        return {
          requestId:
            data.result?.requestId ||
            requestIds?.[0] ||
            `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          requestIds
        };
      } else {
        throw new Error(`SMS 발송 실패: ${data.message}`);
      }
    } catch (error: any) {
      console.error('SMS 발송 오류:', error);
      
      if (error.code) {
        switch (error.code) {
          case 'functions/unauthenticated':
            throw new Error('로그인이 필요합니다.');
          case 'functions/invalid-argument':
            throw new Error('전화번호와 메시지를 입력해주세요.');
          case 'functions/internal':
            throw new Error('서버 오류가 발생했습니다.');
          default:
            throw new Error(`SMS 발송에 실패했습니다: ${error.message}`);
        }
      }
      
      throw new Error(`SMS 발송에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  }

  // 디버그 테스트
  static async debugTest(data: any = {}): Promise<any> {
    try {
      const debugSmsTest = httpsCallable(functions, 'debugSmsTest');
      const result = await debugSmsTest(data);
      console.log('디버그 테스트 결과:', result.data);
      return result.data;
    } catch (error) {
      console.error('디버그 테스트 실패:', error);
      throw error;
    }
  }
}

export default SmsService.getInstance(); 
